import type { ComponentProps, ReactNode } from 'react';

import type { ChassisTrace } from './atoms/ChassisTraces';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { ChassisTraces } from './atoms/ChassisTraces';
import { chassisVars } from './atoms/utils';

export type { ChassisBracketProps } from './atoms/ChassisBracket';
export { ChassisBracket } from './atoms/ChassisBracket';
export type { ChassisGapProps } from './atoms/ChassisGap';
export { ChassisGap } from './atoms/ChassisGap';
export type { ChassisGroupProps } from './atoms/ChassisGroup';
export { ChassisGroup } from './atoms/ChassisGroup';
export { ChassisRear } from './atoms/ChassisRear';
export { ChassisRow } from './atoms/ChassisRow';
export type { ChassisSectionProps } from './atoms/ChassisSection';
export { ChassisSection } from './atoms/ChassisSection';
export type {
  ChassisTrace,
  ChassisTraceEdge,
  ChassisTracesProps,
  ChassisTraceVariant,
} from './atoms/ChassisTraces';
export { ChassisTraces, useChassisTraceAnchor } from './atoms/ChassisTraces';
export type { ChassisUnitLabelProps, ChassisUnitProps } from './atoms/ChassisUnit';
export { ChassisUnit } from './atoms/ChassisUnit';
export { chassisVars } from './atoms/utils';

const ChassisMetadata = styled('div', { display: 'flex' });
ChassisMetadata.displayName = 'ChassisMetadata';

const ChassisDetails = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  gap: '$4',
  maxWidth: '$160',
  minWidth: 0,
  padding: `$${chassisVars.padding}`,
});
ChassisDetails.displayName = 'ChassisDetails';

const ChassisUnits = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: `$${chassisVars.gap.unit}`,
  width: 'fit-content',
  minWidth: 'fit-content',
  padding: `$${chassisVars.padding}`,
  alignItems: 'end',
});
ChassisUnits.displayName = 'ChassisUnits';

const ChassisContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  width: 'fit-content',
  minWidth: 0,
  minHeight: 0,
  overflow: 'visible',
  background: colors.bgApplicationLight,
  borderRadius: `$${chassisVars.radius}`,
  boxShadow: shadows.deviceLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.deviceDark,
  },
});
ChassisContainer.displayName = 'ChassisContainer';

export type ChassisProps = {
  children?: ReactNode;
  metadata?: ReactNode;
  /**
   * Lines drawn between chips on this card, joining ports that belong to one thing — the ports of a
   * SPAN session, say. Endpoints name chips by their `traceId`; a trace naming one this card
   * doesn't draw is skipped.
   */
  traces?: ChassisTrace[];
} & ComponentProps<typeof ChassisContainer>;

export function Chassis({ children, metadata, traces, ...remaining }: ChassisProps) {
  return (
    <ChassisContainer {...remaining}>
      {/* Inside the card's positioned box, since that is the plane the lines are measured against. */}
      <ChassisTraces traces={traces}>
        <ChassisUnits>{children}</ChassisUnits>
      </ChassisTraces>
      {metadata && (
        <ChassisDetails>
          <ChassisMetadata>{metadata}</ChassisMetadata>
        </ChassisDetails>
      )}
    </ChassisContainer>
  );
}
